type HyperDXBrowser = {
  init: (config: {
    apiKey: string;
    service: string;
    tracePropagationTargets?: (string | RegExp)[];
    consoleCapture?: boolean;
    advancedNetworkCapture?: boolean;
    ignoreUrls?: (string | RegExp)[];
    url?: string;
  }) => void;
  setGlobalAttributes: (attributes: Record<string, string>) => void;
};

type CompanyLike = {
  id?: string;
  _id?: string;
  name?: string;
  companyTypes?: string[] | string;
};

export type AuthSessionUser = {
  email?: string;
  userInfo?: {
    id?: string;
    _id?: string;
    sub?: string;
    email?: string;
    name?: string;
    firstname?: string;
    lastname?: string;
    userCompany?: CompanyLike | null;
  } | null;
};

let client: HyperDXBrowser | null = null;
let attributes: Record<string, string> = {};

const asString = (value: unknown): string => {
  if (value == null) return "";
  if (Array.isArray(value)) return value.filter(Boolean).join(",");
  return String(value).trim();
};

const compact = (input: Record<string, string>): Record<string, string> =>
  Object.fromEntries(Object.entries(input).filter(([, value]) => value !== ""));

export const buildAuthenticatedSessionAttributes = (
  user: AuthSessionUser | null | undefined
): Record<string, string> => {
  const userInfo = user?.userInfo ?? {};
  const company = userInfo.userCompany ?? {};
  const firstname = asString(userInfo.firstname);
  const lastname = asString(userInfo.lastname);
  const fullName = [firstname, lastname].filter(Boolean).join(" ");
  const companyId = asString(company._id || company.id);
  const companyName = asString(company.name);

  return compact({
    userId: asString(userInfo.id || userInfo._id),
    userEmail: asString(user?.email || userInfo.email),
    userName: asString(
      fullName || userInfo.name || user?.email || userInfo.email
    ),
    teamName: companyName,
    teamId: companyId,
    companyId,
    companyName,
    companyTypes: asString(company.companyTypes),
    auth0Id: asString(userInfo.sub),
    sessionKind: "authenticated",
  });
};

const flushAttributes = () => {
  if (!client || Object.keys(attributes).length === 0) return;
  client.setGlobalAttributes(attributes);
};

const patchAttributes = (patch: Record<string, string>) => {
  attributes = compact({ ...attributes, ...patch });
  flushAttributes();
};

export const identifyAuthenticatedUser = (
  user: AuthSessionUser | null | undefined
) => {
  if (typeof window === "undefined") return;
  patchAttributes(buildAuthenticatedSessionAttributes(user));
};

export const tagShippingSession = ({
  shippingRecordId,
  shippingAccess,
}: {
  shippingRecordId?: string;
  shippingAccess: "public" | "internal";
}) => {
  if (typeof window === "undefined" || !shippingRecordId) return;
  patchAttributes({
    shippingRecordId: asString(shippingRecordId),
    shippingAccess,
  });
};

export const clearHyperDXUser = () => {
  if (typeof window === "undefined") return;
  const shippingRecordId = attributes.shippingRecordId ?? "";
  const shippingAccess = attributes.shippingAccess ?? "";
  attributes = {
    userId: "",
    userEmail: "",
    userName: "",
    teamName: "",
    teamId: "",
    companyId: "",
    companyName: "",
    companyTypes: "",
    auth0Id: "",
    sessionKind: "anonymous",
    ...(shippingRecordId ? { shippingRecordId } : {}),
    ...(shippingAccess ? { shippingAccess } : {}),
  };
  // Empty identity fields must be sent so a merged SDK state cannot keep the previous user.
  if (client) {
    client.setGlobalAttributes(attributes);
  }
};

export const initHyperDX = async () => {
  const apiKey = import.meta.env.VITE_HYPERDX_API_KEY;

  if (!apiKey || typeof window === "undefined") {
    return;
  }

  try {
    const { default: HyperDX } = await import("@hyperdx/browser");

    HyperDX.init({
      apiKey,
      service:
        (import.meta.env.VITE_HYPERDX_SERVICE_NAME as string) ||
        "batail-log-frontend",
      tracePropagationTargets: [
        /localhost/i,
        /batail/i,
        /noria/i,
        /cleverapps\.io/i,
      ],
      consoleCapture: true,
      advancedNetworkCapture: true,
      // Login still sends email+password as query params; do not ingest them.
      ignoreUrls: [/\/api\/login/i, /\/api\/reset/i],
      url: (import.meta.env.VITE_HYPERDX_URL as string) || undefined,
    });

    client = HyperDX;
    flushAttributes();
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn("HyperDX initialization skipped", error);
  }
};

export const resetHyperDXForTests = () => {
  client = null;
  attributes = {};
};
